import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { Button, Divider, Grid, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import { makeStyles } from '@mui/styles';
// The Trips grids render as DataGridPro (flavor 'pro' below), so the filter operators must
// come from the pro package: the v5 operators' input components look up the v5 grid context
// and throw inside a v6 grid.
import { getGridStringOperators } from '@mui/x-data-grid-pro';
import clsx from 'clsx';
import moment from 'moment';
import * as momentTimezone from 'moment-timezone';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React from 'react';
import { useHistory } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import EditIcon from '@mui/icons-material/BorderColorOutlined';

import { DataAPIContext } from '../api';
import { DELETE_BATCH, GET_LIST, GET_ONE } from '../api/dataProvider';
import { isBackgroundTaskResponse, notifyBackgroundTask } from '../bg_tasks/batchResponseHelper';
import { BackgroundTaskContext } from '../bg_tasks/BackgroundTaskProvider';
import ConfirmationDialog from '../components/ConfirmationDialog';
import EntityExportDialog from '../components/EntityExportDialog';
import ExportMenuButton from '../components/ExportMenuButton';
import FixedLinearProgress from '../components/FixedLinearProgress';
import useGridExport from '../components/useGridExport';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import { getLicenseeColumn } from '../components/franchiseColumns';
import { a11yProps } from '../components/TabPanel';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import launchPolicyLabel from './launchPolicyLabel';
import TripListToolbar from './TripListToolbar';
import TripPreview from './TripPreview';
import { ConfirmationDialogText, TourUsers } from './utils';

const useStyles = makeStyles((theme) => ({
    gridRoot: {
        background: 'transparent',
        [theme.breakpoints.down('sm')]: {
            padding: '0rem',
        },
    },
    recordListRoot: {
    },
    recordList: {
        borderRadius: '0 0 15px 15px !important',
        height: `calc(${theme.cp.contentHeight} - 11rem) !important`,
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none !important',
        },
    },
    tabPanel: {
        height: '100%',
        position: 'relative',
        overflowY: 'auto',
        border: 0,
    },
    tabsRoot: {
        height: `calc(${theme.cp.contentHeight} - 10rem)`,
        overflowY: 'inherit',
        [theme.breakpoints.down('sm')]: {
            height: `calc(${theme.cp.contentHeight} - 4rem) !important`,
        },
    },
    detailsGridCell: {
        paddingTop: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            padding: '1rem',
            paddingTop: theme.spacing(6),
        },
    },
    listItem: {
        border: '1px solid #d7d7d7',
        borderRadius: '15px',
        padding: theme.spacing(1),
        margin: theme.spacing(1),
    },
    tabsMenu: {
        border: '1px solid #d8d8d8',
        borderRadius: '15px 15px 0 0 !important',
        [theme.breakpoints.down('sm')]: {
            border: 'none',
            paddingTop: '0.35rem',
        },
    },
    listToolbarWrapper: {
        borderLeft: '1px solid #d8d8d8',
        borderRight: '1px solid #d8d8d8',
        [theme.breakpoints.down('sm')]: {
            border: 'none',
            paddingTop: '1rem',
            marginBottom: '-0.5rem !important',
        },
    },
}));

// Define common columns outside the component to avoid duplication
const hasExtAuxIdColumn = {
    field: 'has_ext_aux_id',
    headerName: 'Has Ext/Aux ID',
    sortable: false,
    filterable: false,
    width: 120, // the header itself needs 115px; at the 100px default it was ellipsized
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => {
        const hasCustomId = !!params.row.custom_id;
        const hasAuxiliaryIds = params.row.additional_custom_ids && Array.isArray(params.row.additional_custom_ids) && params.row.additional_custom_ids.length > 0;
        const hasValue = hasCustomId || hasAuxiliaryIds;
        return hasValue ? (
            <Tooltip title="Has external/auxiliary ID">
                <CheckIcon fontSize="small" />
            </Tooltip>
        ) : null;
    }
};

const systemTagsColumn = {
    field: 'system_tags',
    headerName: 'System Tags',
    sortable: false,
    filterable: false, // Keep filterable false as per previous definition
    flex: 1,
    renderCell: (params) => {
        const systemTags = params.row.tags;
        if (!systemTags || !Array.isArray(systemTags) || systemTags.length === 0) {
            return null;
        }
        const formatTagName = (tag) => {
            const categoryName = tag.category?.name || 'Uncategorized';
            return `${categoryName}: ${tag.name}`;
        }

        if (systemTags.length === 1) {
            return formatTagName(systemTags[0]);
        } else {
            const tooltipTitle = systemTags.map(tag => (
                <div key={tag.id}>{formatTagName(tag)}</div>
            ));
            const firstTagFormatted = formatTagName(systemTags[0]);
            const remainingCount = systemTags.length - 1;
            return (
                <Tooltip title={<>{tooltipTitle}</>}>
                    <Typography variant="body2" component="span" sx={{ cursor: 'default', display: 'inline-flex', alignItems: 'center' }}>
                        {firstTagFormatted}
                        {remainingCount > 0 && (
                            <Typography variant="caption" component="span" sx={{ ml: 0.5 }}>
                                (+{remainingCount})
                            </Typography>
                        )}
                    </Typography>
                </Tooltip>
            );
        }
    }
};

const timezoneColumn = {
    field: 'start_timezone',
    headerName: 'Timezone',
    sortable: true,
    filterable: true,
    flex: 0.8,
    valueGetter: (p) => {
        const tz = p.row.start_timezone;
        if (!tz) return '';
        const tzOffset = momentTimezone.tz(tz).format('Z');
        return `${tz} (GMT${tzOffset})`;
    }
};

const PRE_CHECK_IN_LIST_LABELS = {
    disabled: 'Not Enabled',
    tour: 'Enabled (trip-level)',
    operator_inherited: 'Enabled (default)',
};

const preCheckInListColumn = {
    field: 'pre_check_in_list_state',
    headerName: 'Pre-check-in',
    sortable: true,
    filterable: true,
    flex: 0.65,
    minWidth: 150,
    type: 'singleSelect',
    valueOptions: Object.keys(PRE_CHECK_IN_LIST_LABELS).map((value) => ({
        value,
        label: PRE_CHECK_IN_LIST_LABELS[value],
    })),
    valueGetter: (p) => p.row.pre_check_in_list_state,
    valueFormatter: (p) => PRE_CHECK_IN_LIST_LABELS[p.value] ?? (p.value || ''),
};

// CP-7968: where the trip's settings come from. The trip's own "Use Tour Operator default settings"
// toggle inherits general settings and tag categories; an operator-wide default without that toggle
// inherits only the tag categories. Same words as the "Trip Settings" column of the xlsx export.
const TRIP_SETTINGS_LIST_LABELS = {
    operator_inherited: 'Tour Operator defaults',
    operator_tag_categories: 'Trip-level (default tag categories)',
    tour: 'Trip-level',
};

const tripSettingsListColumn = {
    field: 'trip_settings_list_state',
    headerName: 'Trip Settings',
    // Computed per row by the backend: its grid filters and ordering cannot use it.
    sortable: false,
    filterable: false,
    flex: 0.65,
    minWidth: 150,
    valueGetter: (p) => p.row.trip_settings_list_state,
    valueFormatter: (p) => TRIP_SETTINGS_LIST_LABELS[p.value] ?? (p.value || ''),
};

const launchPolicyColumn = {
    field: 'launch_policy',
    headerName: 'Launch Policy',
    sortable: true,
    filterable: true,
    flex: 0.5,
    valueGetter: (p) => launchPolicyLabel(p.row),
};

// CP-8309: staff-only alias for the trip. The list search (?query=) matches it
// server-side alongside Name, so it is shown so a hit on it is explainable.
const internalNameColumn = {
    field: 'internal_name',
    headerName: 'Internal Name',
    sortable: true,
    filterable: true,
    flex: 0.8,
    valueGetter: (p) => p.row.internal_name || '',
    filterOperators: getGridStringOperators().filter((op) => op.value === 'contains'),
};

const TripList = (props) => {
    const history = useHistory();
    const dataAPI = React.useContext(DataAPIContext);
    const { hasPerm, isFranchise, licensees, currentLicensee } = React.useContext(ContextStore);
    const resource = 'tour';
    const classes = useStyles();
    const isSmallScreen = !useIsWidthUp('sm');
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const bgCtx = React.useContext(BackgroundTaskContext);
    const urlSearchParams = new URLSearchParams(window.location.search);
    const [activeTripId, setActiveTripId] = React.useState(parseInt(urlSearchParams.get('id')));
    const [query, setQuery] = React.useState(urlSearchParams.get('query') || null);
    const [selectedIds, setSelectedIds] = React.useState([]);
    const [lastUpdate, setLastUpdate] = React.useState(null);
    const [syncActive, setSyncActive] = React.useState(false);
    const [currentTab, setCurrentTab] = React.useState(parseInt(urlSearchParams.get('tab')) || 0);
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [idsToDelete] = React.useState(null);
    const [relatedLoading, setRelatedLoading] = React.useState(false);
    const cols = isSmallScreen ? (activeTripId ? 0 : 12) : activeTripId ? 8 : 12;

    // Stable references for apiFilters — inline arrays cause RecordList to
    // refetch data on every TripList re-render because the data-fetch useEffect
    // includes props.apiFilters in its dependency array.
    const apiFiltersActive = React.useMemo(() => [{ field: 'time', value: 'active' }], []);
    const apiFiltersUpcoming = React.useMemo(() => [{ field: 'time', value: 'upcoming' }], []);
    const apiFiltersPast = React.useMemo(() => [{ field: 'time', value: 'past' }], []);
    const apiFiltersNonBespoke = React.useMemo(() => [{ field: 'time', value: 'nonbespoke' }], []);
    const apiFiltersGuideGroups = React.useMemo(() => [{ field: 'time', value: 'guide_groups' }], []);

    // CP-8136: the Guide Groups tab renders only for operators that actually
    // have guide-group tours (Ben/Jordan). One page-size-1 probe on mount; the
    // backend filter is operator- and franchise-scoped already. `|| currentTab
    // === 4` below keeps a deep link onto the tab working while the probe is
    // in flight; the effect snaps back to Active if the probe says none exist.
    const [hasGuideGroups, setHasGuideGroups] = React.useState(false);
    React.useEffect(() => {
        let cancelled = false;
        dataAPI(GET_LIST, resource, {
            filter: { filters: JSON.stringify([{ field: 'time', value: 'guide_groups' }]) },
            pagination: { page: 1, perPage: 1 },
            sort: { field: 'name', order: 'ASC' },
        }).then((res) => {
            if (cancelled) { return; }
            const total = res?.total || 0;
            setHasGuideGroups(total > 0);
            if (total === 0 && currentTab === 4) {
                setCurrentTab(0);
            }
        }).catch(() => {});
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataAPI, resource]);

    // XML/JSON entity export of the current tab's tours (selection narrows it).
    // The menu item that was clicked pre-selects the format in the dialog.
    const [tourExportOpen, setTourExportOpen] = React.useState(false);
    const [tourExportFormat, setTourExportFormat] = React.useState(null);
    const openTourExport = (format) => {
        setTourExportFormat(typeof format === 'string' ? format : null);
        setTourExportOpen(true);
    };
    const currentTabFilters = [
        apiFiltersActive, apiFiltersUpcoming, apiFiltersPast, apiFiltersNonBespoke, apiFiltersGuideGroups,
    ][currentTab] || apiFiltersActive;
    const { exportDocument: exportTourDocument, exporting: tourExporting } = useGridExport({
        resource,
        apiFilters: currentTabFilters,
        query,
        selectedIds,
        isAllSelected: false,
    });


    const handleTabChange = (ev, newTab) => {
        setActiveTripId(null);
        setCurrentTab(newTab);
        setSelectedIds([]);
        urlSearchParams.set('tab', newTab);
        urlSearchParams.delete('id');
        props.setLastURLParams(urlSearchParams)
        history.push(`${window.location.pathname}?${urlSearchParams.toString()}`);
        setLastUpdate(new Date().toString());
    };



    const onCloseDetails = () => {
        setActiveTripId(null);
        urlSearchParams.delete('id');
        props.setLastURLParams(urlSearchParams)
        history.push(`/trip?${urlSearchParams.toString()}`);
    };

    const onQueryChange = (query) => {
        setQuery(query);
        if (!query) {
            urlSearchParams.delete('query');
        } else {
            urlSearchParams.set('query', query);
        }
        props.setLastURLParams(urlSearchParams)
        history.push(`${window.location.pathname}?${urlSearchParams.toString()}`);
    };
    const onSelectionModelChange = (ids, selection) => {
        setSelectedIds(selection);
        if (selection.length === 1) {
            setActiveTripId(selection[0]);
            urlSearchParams.set('id', selection[0]);
            props.setLastURLParams(urlSearchParams)
            history.push(`/trip?${urlSearchParams.toString()}`);
        }
        else {
            urlSearchParams.delete('id');
            props.setLastURLParams(urlSearchParams)
            history.push(`/trip?${urlSearchParams.toString()}`);
            setActiveTripId(null);
        }
    };


    const onTripDeleted = () => {
        setLastUpdate(new Date().toString());
        setActiveTripId(null);
    };

    const doDeleteTripList = (idlist) => {
        setRelatedLoading(true);
        setSyncActive(true);
        dataAPI(DELETE_BATCH, resource, { data: { list: idlist } })
            .then((res) => {
                if (isBackgroundTaskResponse(res)) {
                    notifyBackgroundTask(res, enqueueSnackbar, closeSnackbar, bgCtx);
                    setSelectedIds([]);
                } else if (res.statusCode === 204 || res.statusCode === 200) {
                    setLastUpdate(new Date().toString());
                    enqueueSnackbar(idlist.length > 1 ? 'Trips deleted' : 'Trip deleted', { variant: 'default' });
                    setSelectedIds([]);
                    urlSearchParams.delete('id');
                    props.setLastURLParams(urlSearchParams)
                    history.push(`/trip?${urlSearchParams.toString()}`);
                } else {
                    enqueueSnackbar('Error deleting trip', { variant: 'error' });
                }
            })
            .catch(() => {
                enqueueSnackbar('Error deleting trip', { variant: 'error' });
            })
            .finally(() => {
                setSelectedIds([]);
                setRelatedLoading(false);
                setSyncActive(false);
            });
    };

    const convert = (data) => {
        data.results = data.results?.map((r) => {
            r._users = new TourUsers(r);
            return r;
        });

        return data;
    };

    const listItemRenderer = (row, idx) => {
        return (
            <div
                className={classes.listItem}
                key={`${row.id}-${idx}`}
                onClick={() => {
                    setActiveTripId(row.id);
                }}
            >
                <Grid container direction="row" spacing={2}>
                    <Grid item xs={10}>
                        <Typography variant="h6">{row.name}</Typography>
                        {row.internal_name && (
                            <Typography variant="body2" color="text.secondary">
                                {row.internal_name}
                            </Typography>
                        )}
                    </Grid>
                    {currentTab !== 3 && currentTab !== 4 && (
                        <Grid item xs={12}>
                            <Typography variant="body1">
                                <strong>Start Date:</strong> {row.start_date}
                            </Typography>
                        </Grid>
                    )}
                    {currentTab !== 3 && currentTab !== 4 && (
                        <Grid item xs={12}>
                            <Typography variant="body1">
                                <strong>End Date:</strong> {row.end_date}
                            </Typography>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Typography variant="body1">
                            <strong>Travelers:</strong> {row.traveler_count}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body1">
                            <strong>Staff:</strong> {row.staff_count}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body1">
                            <strong>Pre-check-in:</strong>{' '}
                            {PRE_CHECK_IN_LIST_LABELS[row.pre_check_in_list_state] ?? row.pre_check_in_list_state ?? '—'}
                        </Typography>
                    </Grid>
                </Grid>
            </div>
        );
    };

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        switch (confirmationDialogAction) {
            case 'delete_trip':
            case 'delete_trips':
                doDeleteTripList(idsToDelete);
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    // CP-4932: fixed-content columns (dates, counts, action icons) get a fixed
    // width sized to what they show, so the flex space goes to Name. The dates
    // use the abbreviated month every other grid in the dashboard uses.
    const columns = [
        ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
        {
            field: 'start_date',
            headerName: 'Start Date',
            sortable: false,
            width: 120,
            valueFormatter: (p) => moment.utc(p.value).format('MMM DD, YYYY'),
        },
        {
            field: 'end_date',
            headerName: 'End Date',
            sortable: false,
            width: 155,
            valueGetter: (p) => {
                return ({ date: p.row.start_date, trip_length: Math.ceil(p.row.length / 86400) })
            },
            valueFormatter: (p) => `${moment(p.value.date).utc(true).add('days', p.value.trip_length === 1 ? p.value.trip_length : (p.value.trip_length - 1)).format('MMM DD, YYYY')} (${p.value.trip_length}d)`,
        },
        { field: 'name', headerName: 'Name', sortable: false, flex: 2 },
        { ...internalNameColumn, sortable: false, filterable: false },
        { field: 'travelers', headerName: 'Travelers', sortable: false, width: 90, valueGetter: (p) => p.row.traveler_count },
        { field: 'staff', headerName: 'Staff', sortable: false, width: 70, valueGetter: (p) => p.row.staff_count },
        launchPolicyColumn,
        preCheckInListColumn,
        tripSettingsListColumn,
        timezoneColumn,
        hasExtAuxIdColumn, // Use the defined constant
        systemTagsColumn, // Use the defined constant
        {
            field: 'atag', headerName: 'Access tags', sortable: false, flex: 0.5, valueGetter: (p) => p.row.atag?.map((x) => x.name).join(', '),
            filterOperators: getGridStringOperators().filter(
                (op) => op.value === 'contains'
            ),
        },
        {
            field: 'buttons',
            headerName: 'Action',
            sortable: false,
            width: 80,
            renderCell: (p) => {
                return (<>
                    <Tooltip title="Edit trip">
                        <RouterLink to={`/trip/${p.id}`} onClick={(e) => e.stopPropagation()} style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                            <EditIcon color="primary" fontSize="small" />
                        </RouterLink>
                    </Tooltip>
                    <Tooltip title="Manage users">
                        <RouterLink to={`/trip/${p.id}/users`} onClick={(e) => e.stopPropagation()} style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                            <PeopleAltIcon color="primary" />
                        </RouterLink>
                    </Tooltip>
                </>);
            },
        },
    ];
    const non_bespoke_columns = [
        ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
        { field: 'name', headerName: 'Name', sortable: true, filterable: true, flex: 2 },
        internalNameColumn,
        { field: 'travelers', headerName: 'Travelers', sortable: false, filterable: false, width: 90, valueGetter: (p) => p.row.traveler_count },
        { field: 'staff', headerName: 'Staff', sortable: false, filterable: false, width: 70, valueGetter: (p) => p.row.staff_count },
        launchPolicyColumn,
        preCheckInListColumn,
        tripSettingsListColumn,
        timezoneColumn,
        hasExtAuxIdColumn, // Use the defined constant
        systemTagsColumn, // Use the defined constant (set filterable: false inside definition)
        {
            field: 'atag', headerName: 'Access tags', sortable: false, flex: 0.5, valueGetter: (p) => p.row.atag?.map((x) => x.name).join(', '),
            filterOperators: getGridStringOperators().filter(
                (op) => op.value === 'contains'
            ),
        },
        {
            field: 'buttons',
            headerName: 'Action',
            sortable: false,
            filterable: false,
            width: 80,
            renderCell: (p) => {
                return (<>
                    <Tooltip title="Edit trip">
                        <RouterLink to={`/trip/${p.id}`} onClick={(e) => e.stopPropagation()} style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                            <EditIcon color="primary" fontSize="small" />
                        </RouterLink>
                    </Tooltip>
                    <Tooltip title="Manage users">
                        <RouterLink to={`/trip/${p.id}/users`} onClick={(e) => e.stopPropagation()} style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                            <PeopleAltIcon color="primary" />
                        </RouterLink>
                    </Tooltip>
                </>);
            },
        },
    ];
    const recordListProps = {
        className: classes.recordList,
        // DataGridPro: gives every column a drag-to-resize header, like the Users grid (CP-4932).
        flavor: 'pro',
        // Without it a dragged width snapped back on the next fetch (re-sort, page change).
        persistColumnWidths: true,
        resource: resource,
        convert: convert,
        disableSelectionOnClick: false,
        checkboxSelection: hasPerm('tour', 'delete'),
        onSelectionModelChange: onSelectionModelChange,
        deps: lastUpdate,
        query: query,
        columns: columns,
        listItemRenderer: listItemRenderer,
        disableListBorder: false,
        relatedLoading: relatedLoading,
        disableColumnMenu: false,
        limitHeightForMobile: 'calc(100vh - 310px)',
    };

    const nonBespokeProps = {
        ...recordListProps,
        columns: non_bespoke_columns,
    };
    // CP-8349: a guide group has no meaningful time zone either.
    const guideGroupProps = {
        ...recordListProps,
        columns: non_bespoke_columns.filter((c) => c !== timezoneColumn),
    };

    const btSize = isSmallScreen ? 'medium' : 'small';
    const createBt = React.useRef(null);

    const onCreateNewTripClick = () => {
        history.push('/trip/create');
    };

    const onExportList = async () => {
        try {
            setSyncActive(true);
            const filters = [];
            switch (currentTab) {
                case 0:
                    filters.push({ field: 'time', value: 'active' });
                    break;
                case 1:
                    filters.push({ field: 'time', value: 'upcoming' });
                    break;
                case 2:
                    filters.push({ field: 'time', value: 'past' });
                    break;
                case 3:
                    filters.push({ field: 'time', value: 'nonbespoke' });
                    break;
                case 4:
                    filters.push({ field: 'time', value: 'guide_groups' });
                    break;
                default:
                    break;
            }

            const params = {
                filter: {
                    query: query,
                    filters: JSON.stringify(filters),
                },
                urlSuffix: 'export/',
            };

            const res = await dataAPI(GET_ONE, resource, params);
            const body = await res.response.blob();
            const url = window.URL.createObjectURL(body);

            let filename = '';
            const disposition = res.headers['Content-Disposition'];
            if (disposition && disposition.indexOf('attachment') !== -1) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) {
                    filename = matches[1].replace(/["']/g, '');
                }
            }
            if (!filename || filename.length <= 0) {
                filename = 'trips.xlsx';
            }

            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            setTimeout(() => {
                document.body.removeChild(a);
            }, 100);
        } catch (error) {
            console.error('onExportList: Error exporting trips list:', error);
        } finally {
            setSyncActive(false);
        }
    };

    return (
        <>
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                </ConfirmationDialog>
            ) : null}
            <FixedLinearProgress visibility={syncActive} />
            {activeTripId && isSmallScreen ? null :
                <Grid item xs={12} container p={2} pb={isSmallScreen ? 1.5 : 2} justifyContent="space-between" alignItems="center">
                    <Grid item xs={'auto'}>
                        <Typography fontWeight={500} fontSize={20}>Trips</Typography>
                    </Grid>
                    <Grid item >
                        {/* {hasPerm('tour', 'delete') && selectedIds && selectedIds.length > 0 && !isSmallScreen ? (
                            <Button color="secondary" variant="outlined" size={btSize} onClick={onDeleteBtClick}>
                                <DeleteIcon />
                                &nbsp;Delete trip{selectedIds.length > 1 ? 's' : ''}
                            </Button>
                        ) : null} */}
                        {/* Icon-only on a phone, like the Rulesets header:
                            three labelled buttons don't fit beside the title. */}
                        {!hasPerm('tour', 'write') || selectedIds?.length > 0 ? null : (
                            <Tooltip title={isSmallScreen ? 'New trip' : ''}>
                                <Button ref={createBt} color="primary" variant="contained" size={btSize} onClick={onCreateNewTripClick}>
                                    <AddIcon />
                                    {!isSmallScreen && <>&nbsp;New trip</>}
                                </Button>
                            </Tooltip>
                        )}
                        {/* One menu, like Users (CP-8289). The spreadsheet is
                            the legacy XLSX of the current tab (it ignores the
                            tick boxes), so it hides once rows are selected.
                            XML/JSON stays: that export is how a selection
                            leaves the page. */}
                        <ExportMenuButton
                            hideLabel={isSmallScreen}
                            size={btSize}
                            disabled={syncActive}
                            sx={{ ml: 1 }}
                            actions={[
                                hasPerm('tour', 'write') && hasPerm('tour', 'read') && !(selectedIds?.length > 0) && {
                                    key: 'xlsx',
                                    label: 'Export as Excel (XLSX)',
                                    icon: <GridOnOutlinedIcon fontSize="small" />,
                                    onClick: onExportList,
                                },
                                hasPerm(resource, 'export') && {
                                    key: 'xml',
                                    label: 'Export as XML (import format)…',
                                    icon: <DescriptionOutlinedIcon fontSize="small" />,
                                    onClick: () => openTourExport('xml'),
                                },
                                hasPerm(resource, 'export') && {
                                    key: 'json',
                                    label: 'Export as JSON (import format)…',
                                    icon: <DescriptionOutlinedIcon fontSize="small" />,
                                    onClick: () => openTourExport('json'),
                                },
                            ]}
                        />
                    </Grid>
                </Grid>}
            {isSmallScreen && <Divider />}
            <Grid container direction="row" className={classes.gridRoot} p={isSmallScreen ? 0 : 2} pt={0}>
                {isSmallScreen && activeTripId ? null : (
                    <Grid item xs={12} sm={cols} className={classes.recordListRoot}>
                        <div className={clsx(classes.tabsRoot, activeTripId && classes.tabsRootActive)}>
                            <Tabs value={currentTab} variant='scrollable' scrollButtons="auto" onChange={handleTabChange} indicatorColor="primary" textColor="primary" className={classes.tabsMenu}>
                                <Tab label="Active" {...a11yProps(0)} />
                                <Tab label="Upcoming" {...a11yProps(1)} />
                                <Tab label="Past" {...a11yProps(2)} />
                                <Tab label="Non-Bespoke" {...a11yProps(3)} />
                                {(hasGuideGroups || currentTab === 4) && <Tab label="Guide Groups" {...a11yProps(4)} />}
                            </Tabs>
                            <Divider />
                            <Grid item className={classes.listToolbarWrapper}>
                                <Grid container direction="row" alignItems="center">
                                    <TripListToolbar query={query} resource={resource} id={activeTripId ? activeTripId : null} onQueryChange={onQueryChange} />
                                </Grid>
                                <EntityExportDialog
                                    open={tourExportOpen}
                                    onClose={() => setTourExportOpen(false)}
                                    entity="tour"
                                    initialFormat={tourExportFormat}
                                    exportDocument={exportTourDocument}
                                    exporting={tourExporting}
                                    selectionLabel={selectedIds.length
                                        ? `${selectedIds.length} selected trips`
                                        : 'all trips matching the current tab and search'}
                                />
                                {!isSmallScreen && <Divider />}
                            </Grid>
                            <Grid item p={isSmallScreen ? 1 : 0}>
                                {currentTab === 0 && <>
                                    <PageMeta title="Active Trips" />
                                    <RecordList {...recordListProps} sortModel={[{ field: 'start_date', sort: 'asc' }]} apiFilters={apiFiltersActive} />
                                </>}
                                {currentTab === 1 && <>
                                    <PageMeta title="Upcoming Trips" />
                                    <RecordList {...recordListProps} sortModel={[{ field: 'start_date', sort: 'asc' }]} apiFilters={apiFiltersUpcoming} />
                                </>}
                                {currentTab === 2 && <>
                                    <PageMeta title="Past Trips" />
                                    <RecordList {...recordListProps} sortModel={[{ field: 'start_date', sort: 'desc' }]} apiFilters={apiFiltersPast} />
                                </>}
                                {currentTab === 3 && <>
                                    <PageMeta title="Non-Bespoke" />
                                    <RecordList {...nonBespokeProps} sortModel={[{ field: 'name', sort: 'asc' }]} apiFilters={apiFiltersNonBespoke} />
                                </>}
                                {currentTab === 4 && <>
                                    <PageMeta title="Guide Groups" />
                                    {/* CP-8136: dates are synthetic on guide groups, so reuse the
                                        date-less Non-Bespoke column set (minus time zone, CP-8349). */}
                                    <RecordList {...guideGroupProps} sortModel={[{ field: 'name', sort: 'asc' }]} apiFilters={apiFiltersGuideGroups} />
                                </>}
                            </Grid>
                        </div>
                    </Grid>
                )}
                {activeTripId ? (
                    <Grid item xs={12} sm={12 - cols} className={classes.detailsGridCell}>
                        <TripPreview resource={resource} id={activeTripId} onClose={onCloseDetails} onDelete={onTripDeleted} setRelatedLoading={setRelatedLoading} />
                    </Grid>
                ) : null}
            </Grid>
        </>
    );
};

TripList.propTypes = {
    setLastURLParams: PropTypes.func.isRequired,
};

export default TripList;
